import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function getStatusColor(status) {
  switch (status) {
    case 'IN_SERVICE':
      return 'bg-emerald-100 text-emerald-800';
    case 'IN_REPAIR':
      return 'bg-amber-100 text-amber-800';
    case 'IN_TRANSFER':
      return 'bg-blue-100 text-blue-800';
    case 'TRASHED':
      return 'bg-red-100 text-red-800';
    case 'PENDING_REMOVAL':
      return 'bg-orange-100 text-orange-800';
    case 'PENDING_EDIT':
      return 'bg-yellow-100 text-yellow-800';
    case 'OTHER':
      return 'bg-purple-100 text-purple-800';
    case 'SOLD':
      return 'bg-cyan-100 text-cyan-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getConditionColor(condition) {
  switch (condition) {
    case 'WORKING':
      return 'bg-green-100 text-green-800';
    case 'BROKEN':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getStatusLabel(status) {
  switch (status) {
    case 'IN_SERVICE':
      return 'In Service';
    case 'IN_REPAIR':
      return 'In Repair';
    case 'IN_TRANSFER':
      return 'In Transfer';
    case 'TRASHED':
      return 'Trashed';
    case 'PENDING_REMOVAL':
      return 'Pending Removal';
    case 'PENDING_EDIT':
      return 'Pending Edit';
    case 'OTHER':
      return 'Other';
    case 'SOLD':
      return 'Sold';
    default:
      return status;
  }
}

export function getEventTypeLabel(eventType) {
  switch (eventType) {
    case 'SCANNED_IN':
      return 'Scanned In';
    case 'SENT_REPAIR':
      return 'Sent to Repair';
    case 'SENT_LOCATION':
      return 'Sent to Location';
    case 'RECEIVED':
      return 'Received';
    case 'SENT_TRASH':
      return 'Trashed';
    case 'UPDATED':
      return 'Updated';
    case 'FIELD_UPDATED':
      return 'Field Updated';
    case 'REMOVAL_REQUESTED':
      return 'Removal Requested';
    case 'REMOVAL_APPROVED':
      return 'Removal Approved';
    case 'REMOVAL_DENIED':
      return 'Removal Denied';
    case 'EDIT_REQUESTED':
      return 'Edit Requested';
    case 'EDIT_APPROVED':
      return 'Edit Approved';
    case 'EDIT_DENIED':
      return 'Edit Denied';
    case 'ASSET_REMOVED':
      return 'Asset Removed';
    // Parent-Child events
    case 'LINKED_TO_PARENT':
      return 'Linked to Parent';
    case 'UNLINKED_FROM_PARENT':
      return 'Unlinked from Parent';
    case 'LINK_REQUESTED':
      return 'Link Requested';
    case 'LINK_APPROVED':
      return 'Link Approved';
    case 'LINK_DENIED':
      return 'Link Denied';
    case 'UNLINK_REQUESTED':
      return 'Unlink Requested';
    case 'UNLINK_APPROVED':
      return 'Unlink Approved';
    case 'UNLINK_DENIED':
      return 'Unlink Denied';
    // Sale events
    case 'SALE_REQUESTED':
      return 'Sale Requested';
    case 'SALE_APPROVED':
      return 'Sale Approved';
    case 'SALE_DENIED':
      return 'Sale Denied';
    case 'ASSET_SOLD':
      return 'Asset Sold';
    default:
      return eventType;
  }
}

export function hasRole(user, role) {
  if (!user || !user.roles) return false;
  return user.roles.includes(role);
}

export function hasAnyRole(user, roles) {
  if (!user || !user.roles) return false;
  return roles.some(role => user.roles.includes(role));
}

export function isAdminOrOps(user) {
  return hasAnyRole(user, ['ADMIN', 'OPS_COORDINATOR']);
}

export function canAccessLocation(user, locationId) {
  if (!user) return false;
  if (isAdminOrOps(user)) return true;
  return (user.assigned_location_ids || []).includes(locationId);
}

export function canUserScan(user) {
  if (!user) return false;
  // All roles can scan (Admin, Ops, Manager)
  return true;
}

export function exportToCSV(data, filename) {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
}
